"use client";

import { resolveApiAssetUrl } from "@/lib/backend";
import { resolveBackgroundVideo } from "@/lib/video-embed";
import { useReducedMotion } from "@/components/hero/useReducedMotion";

type Props = {
  videoUrl: string;
  providerHint?: string;
  posterUrl?: string;
};

export function HeroBackgroundVideo({ videoUrl, providerHint, posterUrl }: Props) {
  const reducedMotion = useReducedMotion();
  const rawUrl = videoUrl.trim();
  if (!rawUrl) return null;

  const { provider, embedSrc, fileSrc } = resolveBackgroundVideo(rawUrl, providerHint);
  const poster = resolveApiAssetUrl(posterUrl);
  const fileVideoSrc = provider === "file" ? resolveApiAssetUrl(fileSrc) : "";

  if (reducedMotion) {
    if (poster) {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={poster}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
      );
    }
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[var(--primary-darker)] via-[var(--primary)] to-[var(--primary-dark)]"
      />
    );
  }

  if (provider === "file" && fileVideoSrc) {
    return (
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={poster || undefined}
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      >
        <source src={fileVideoSrc} />
      </video>
    );
  }

  if (embedSrc) {
    return (
      <iframe
        src={embedSrc}
        title=""
        aria-hidden
        tabIndex={-1}
        loading="eager"
        allow="autoplay; fullscreen; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2 border-0"
      />
    );
  }

  return null;
}
